/**
 * Which of a wall-seated box's own dimension axes runs ALONG its wall —
 * i.e. which one holds its width, leaving the other as depth.
 *
 * A cabinet's or countertop's `dimensions` are in its OWN frame, with
 * orientation carried separately in `rotation.y` — so neither axis is
 * inherently "width". The old test for this was `dimensions.z > dimensions.x`,
 * which is not a rotation test at all: it asks "is this box deeper than it is
 * wide", so it fired on every box narrower than its own depth regardless of
 * orientation.
 *
 * The wall is the only thing that can answer the question, and every caller
 * already has it. Project both local axes onto the wall direction and take
 * whichever lines up: that is the width axis, the other is depth.
 *
 * Correct for kitchen runs as well as vanities — width follows the wall in
 * both cases, where a size heuristic would mistake a narrow kitchen counter
 * segment for a rotated vanity.
 *
 * Local-axis convention matches `calculateCorners`
 * (`shared-lib/bathroom-analyzer/pipeline/geometry.js`): a local +x offset
 * maps to world `(cos, sin)`, a local +z offset to world `(-sin, cos)`.
 *
 * @param {number} rotationY - the object's rotation about Y, radians
 * @param {{x: number, z: number}} wallDir - unit vector along the wall
 * @returns {'x' | 'z'} the dimension key holding the along-wall width
 */
function alongWallAxis(rotationY, wallDir) {
  const angle = typeof rotationY === 'number' ? rotationY : 0;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  // Local +x and +z as world direction vectors.
  const alongLocalX = Math.abs(cos * wallDir.x + sin * wallDir.z);
  const alongLocalZ = Math.abs(-sin * wallDir.x + cos * wallDir.z);

  return alongLocalX >= alongLocalZ ? 'x' : 'z';
}

module.exports = { alongWallAxis };
